var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AliasTable } from "./aliases.js";
export class KeyPassphraseRequiredError extends Error {
    constructor(label) {
        super(`Key '${label}' is not unlocked`);
        this.label = label;
        this.name = 'KeyPassphraseRequiredError';
    }
}
export class RdbSession {
    constructor(options) {
        var _a, _b;
        this.variables = new Map();
        this.aliases = new AliasTable();
        this.unlocked = new Map();
        this.workspace = options.workspace;
        this.keyVault = options.keyVault;
        this.refAutoUpdate = (_a = options.refAutoUpdate) !== null && _a !== void 0 ? _a : 'off';
        this.createUuidFn = (_b = options.createUuid) !== null && _b !== void 0 ? _b : (() => globalThis.crypto.randomUUID());
    }
    createKey(label, passphrase) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.keyVault === undefined)
                throw new Error('No key vault configured');
            const identity = yield this.keyVault.create(label, passphrase);
            this.unlocked.set(identity.keyId, identity);
            return identity;
        });
    }
    unlockKey(labelOrPrefix, passphrase) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.keyVault === undefined)
                throw new Error('No key vault configured');
            const identity = yield this.keyVault.unlock(this.resolveKeyRef(labelOrPrefix), passphrase);
            this.unlocked.set(identity.keyId, identity);
            return identity;
        });
    }
    selectAuthor(labelOrPrefix) {
        if (this.keyVault === undefined)
            throw new Error('No key vault configured');
        const record = this.keyVault.resolveRecord(this.resolveKeyRef(labelOrPrefix));
        const identity = this.unlocked.get(record.keyId);
        if (identity === undefined)
            throw new Error(`Key '${record.label}' is locked`);
        this.currentAuthorKeyId = identity.keyId;
        return identity;
    }
    clearAuthor() {
        this.currentAuthorKeyId = undefined;
    }
    isUnlocked(keyId) {
        return this.unlocked.has(keyId);
    }
    setCurrentDatabase(id) {
        this.currentDatabase = id;
    }
    setCurrentGroup(id) {
        this.currentGroup = id;
    }
    setDefaultView(view) {
        this.defaultView = view;
    }
    clearDefaultView() {
        this.defaultView = undefined;
    }
    setRefAutoUpdate(mode) {
        this.refAutoUpdate = mode;
    }
    setVariable(name, value) {
        this.variables.set(name, value);
    }
    selectedAuthor() {
        return this.currentAuthorKeyId === undefined ? undefined : this.unlocked.get(this.currentAuthorKeyId);
    }
    currentAuthor() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.selectedAuthor();
        });
    }
    resolveIdentity(labelOrPrefix) {
        if (this.keyVault === undefined)
            throw new Error('No key vault configured');
        const record = this.keyVault.resolveRecord(this.resolveKeyRef(labelOrPrefix));
        return this.unlocked.get(record.keyId);
    }
    resolveAuthor(ref) {
        return __awaiter(this, void 0, void 0, function* () {
            const labelOrPrefix = ref.kind === 'variable'
                ? this.resolveKeyRef(ref.name)
                : `#${ref.prefix}`;
            const identity = this.resolveIdentity(labelOrPrefix);
            if (identity !== undefined)
                return identity;
            const record = this.keyVault.resolveRecord(labelOrPrefix);
            throw new KeyPassphraseRequiredError(record.label);
        });
    }
    resolveVariable(name) {
        return __awaiter(this, void 0, void 0, function* () {
            if (name === 'me' || name === 'author') {
                const identity = yield this.currentAuthor();
                if (identity !== undefined)
                    return identity;
            }
            const explicit = this.variables.get(name);
            if (explicit !== undefined)
                return explicit;
            const keyAlias = this.aliases.get('key', name);
            if (keyAlias !== undefined) {
                if (this.keyVault === undefined)
                    throw new Error('No key vault configured');
                const record = this.keyVault.resolvePublic(keyAlias);
                return { keyId: record.keyId, publicKey: record.publicKey };
            }
            if (this.keyVault !== undefined) {
                try {
                    const publicKey = this.keyVault.resolvePublic(name);
                    return { keyId: publicKey.keyId, publicKey: publicKey.publicKey };
                }
                catch (_e) {
                    // Keep the user-facing error centered on the missing variable.
                }
            }
            throw new Error(`Unknown variable '$${name}'`);
        });
    }
    resolvePublicKey(labelOrPrefix) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.keyVault === undefined)
                throw new Error('No key vault configured');
            const record = this.keyVault.resolvePublic(this.resolveKeyRef(labelOrPrefix));
            return { keyId: record.keyId, publicKey: record.publicKey };
        });
    }
    resolveKeyRef(labelOrPrefix) {
        if (labelOrPrefix.startsWith('#'))
            return labelOrPrefix;
        const aliased = this.aliases.get('key', labelOrPrefix);
        return aliased !== null && aliased !== void 0 ? aliased : labelOrPrefix;
    }
    createUuid() {
        return this.createUuidFn();
    }
    createSeed(kind, name) {
        const hash = this.workspace.replica.getHashSuite();
        return hash.hashToB64(new TextEncoder().encode(`${kind}:${name !== null && name !== void 0 ? name : ''}:${this.createUuid()}`));
    }
}
